import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Factory, Pencil, Plus, Search, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { cn } from '@/lib/utils';
import {
  useCreateWorkstation,
  useDeleteWorkstation,
  useSetItemWorkstations,
  useUpdateWorkstation,
  useWorkstations,
  useWorkstationsForItem,
  type Workstation,
} from '@/features/inventory/hooks';

/**
 * Workstation picker for the item detail page.
 *
 * A component can be associated with zero or many workstations. The list
 * of workstations is a *curated* per-org vocabulary (migration 94): the
 * dropdown offers the org's existing stations to toggle on/off, plus an
 * inline "add" affordance and rename / delete controls. Because operators
 * pick from the list instead of free-typing, the catalog never forks into
 * "Base" vs "Bases" — which is exactly what lets the grid facet-filter by
 * a single canonical "Base" value.
 *
 * Self-contained: give it an `itemId` + `canEdit` and it owns its own
 * queries / mutations against `workstations` + `item_workstations`.
 */
export function WorkstationsPicker({
  itemId,
  canEdit,
  className,
}: {
  itemId: string;
  canEdit: boolean;
  className?: string;
}) {
  const { data: assigned, isLoading: assignedLoading } = useWorkstationsForItem(itemId);
  const { data: catalog, isLoading: catalogLoading } = useWorkstations();
  const setItemWorkstations = useSetItemWorkstations();
  const createWorkstation = useCreateWorkstation();
  const deleteWorkstation = useDeleteWorkstation();
  const updateWorkstation = useUpdateWorkstation();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const assignedIds = useMemo(() => new Set((assigned ?? []).map((w) => w.id)), [assigned]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setRenamingId(null);
      setDraftName('');
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return catalog ?? [];
    return (catalog ?? []).filter((w) => w.name.toLowerCase().includes(q));
  }, [catalog, query]);

  const exactMatch = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    return (catalog ?? []).find((w) => w.name.toLowerCase() === q) ?? null;
  }, [catalog, query]);

  const trimmedQuery = query.trim();
  const showCreate = canEdit && trimmedQuery.length > 0 && !exactMatch;

  const toggle = async (ws: Workstation) => {
    const currentIds = (assigned ?? []).map((w) => w.id);
    const next = new Set(currentIds);
    if (next.has(ws.id)) next.delete(ws.id);
    else next.add(ws.id);
    try {
      await setItemWorkstations.mutateAsync({
        itemId,
        workstationIds: Array.from(next),
        currentIds,
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update workstations.');
    }
  };

  const removeAssigned = async (wsId: string) => {
    const currentIds = (assigned ?? []).map((w) => w.id);
    if (!currentIds.includes(wsId)) return;
    try {
      await setItemWorkstations.mutateAsync({
        itemId,
        workstationIds: currentIds.filter((id) => id !== wsId),
        currentIds,
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not remove workstation.');
    }
  };

  const createAndAttach = async () => {
    const name = trimmedQuery;
    if (!name) return;
    try {
      const ws = await createWorkstation.mutateAsync({ name });
      const currentIds = (assigned ?? []).map((w) => w.id);
      if (!currentIds.includes(ws.id)) {
        await setItemWorkstations.mutateAsync({
          itemId,
          workstationIds: [...currentIds, ws.id],
          currentIds,
        });
      }
      setQuery('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not create workstation.');
    }
  };

  const removeFromCatalog = async (ws: Workstation) => {
    const ok = confirm(
      `Delete the “${ws.name}” workstation for the whole organisation? It will be removed from every item that uses it.`,
    );
    if (!ok) return;
    try {
      await deleteWorkstation.mutateAsync({ id: ws.id });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete workstation.');
    }
  };

  const startRename = (ws: Workstation) => {
    setRenamingId(ws.id);
    setDraftName(ws.name);
  };

  const cancelRename = () => {
    setRenamingId(null);
    setDraftName('');
  };

  const commitRename = async (ws: Workstation) => {
    const next = draftName.trim();
    if (!next || next === ws.name) {
      cancelRename();
      return;
    }
    try {
      await updateWorkstation.mutateAsync({ id: ws.id, patch: { name: next } });
      cancelRename();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not rename workstation.');
    }
  };

  // ---------------- Read-only render ----------------
  if (!canEdit) {
    return (
      <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
        {assignedLoading ? (
          <span className="text-xs text-muted-foreground">…</span>
        ) : (assigned ?? []).length === 0 ? (
          <span className="text-xs text-muted-foreground">—</span>
        ) : (
          (assigned ?? []).map((w) => <WorkstationBadge key={w.id} workstation={w} />)
        )}
      </div>
    );
  }

  // ---------------- Editable render ----------------
  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      {(assigned ?? []).map((w) => (
        <span key={w.id} className="inline-flex items-center">
          <WorkstationBadge workstation={w} />
          <button
            type="button"
            onClick={() => void removeAssigned(w.id)}
            className="-ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            aria-label={`Remove ${w.name} workstation`}
            title={`Remove ${w.name}`}
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}

      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-2 py-0.5 text-xs text-muted-foreground transition-colors hover:border-foreground/40 hover:bg-secondary/60 hover:text-foreground"
            aria-label="Add workstation"
          >
            <Plus className="h-3 w-3" />
            <span>{(assigned ?? []).length === 0 ? 'Add workstations' : 'Add'}</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72 p-0">
          <div className="flex items-center gap-2 border-b border-border px-3 py-2">
            <Search className="h-3.5 w-3.5 text-muted-foreground" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && showCreate) {
                  e.preventDefault();
                  void createAndAttach();
                }
              }}
              placeholder="Search or add…"
              maxLength={64}
              className="w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
            />
          </div>

          <div className="max-h-64 overflow-y-auto p-1">
            {catalogLoading ? (
              <div className="px-3 py-4 text-center text-xs text-muted-foreground">Loading…</div>
            ) : filtered.length === 0 && !showCreate ? (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                No workstations yet — start typing to add one.
              </div>
            ) : (
              filtered.map((ws) => {
                const checked = assignedIds.has(ws.id);
                const isRenaming = renamingId === ws.id;
                const renameBusy = updateWorkstation.isPending;
                return (
                  <div
                    key={ws.id}
                    className="group flex w-full items-center gap-2 rounded-md px-2 py-1 hover:bg-secondary/60"
                  >
                    {isRenaming ? (
                      <div className="flex min-w-0 flex-1 items-center gap-1">
                        <input
                          autoFocus
                          value={draftName}
                          onChange={(e) => setDraftName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              void commitRename(ws);
                            } else if (e.key === 'Escape') {
                              e.preventDefault();
                              cancelRename();
                            }
                          }}
                          maxLength={64}
                          disabled={renameBusy}
                          aria-label={`Rename ${ws.name} workstation`}
                          className="min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-0.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-60"
                        />
                        <button
                          type="button"
                          onClick={() => void commitRename(ws)}
                          disabled={renameBusy}
                          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:opacity-60"
                          aria-label="Save rename"
                          title="Save"
                        >
                          <Check className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={cancelRename}
                          disabled={renameBusy}
                          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:opacity-60"
                          aria-label="Cancel rename"
                          title="Cancel"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => void toggle(ws)}
                          className="flex min-w-0 flex-1 items-center gap-2 text-left"
                        >
                          <span
                            className={cn(
                              'flex h-4 w-4 shrink-0 items-center justify-center rounded border border-border',
                              checked
                                ? 'border-primary bg-primary text-primary-foreground'
                                : 'opacity-60',
                            )}
                          >
                            {checked && <Check className="h-3 w-3" />}
                          </span>
                          <WorkstationBadge workstation={ws} />
                        </button>
                        <button
                          type="button"
                          onClick={() => startRename(ws)}
                          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity hover:bg-secondary hover:text-foreground group-hover:opacity-100"
                          aria-label={`Rename ${ws.name} workstation`}
                          title="Rename workstation"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => void removeFromCatalog(ws)}
                          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
                          aria-label={`Delete ${ws.name} workstation`}
                          title="Delete workstation"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {showCreate && (
            <div className="border-t border-border p-2">
              <p className="px-1 pb-2 text-xs text-muted-foreground">Add new workstation</p>
              <button
                type="button"
                onClick={() => void createAndAttach()}
                disabled={createWorkstation.isPending || setItemWorkstations.isPending}
                className="flex w-full items-center gap-2 rounded-md border border-border px-2 py-1.5 text-left text-sm transition-colors hover:bg-secondary/60 disabled:opacity-60"
              >
                <Plus className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate font-medium">{trimmedQuery}</span>
              </button>
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/** Neutral pill for a workstation. No colour axis — workstations are a
 *  plain vocabulary, distinct from the colour-coded item tags. */
function WorkstationBadge({ workstation }: { workstation: Workstation }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary/60 px-2 py-0.5 text-xs font-medium text-foreground">
      <Factory className="h-2.5 w-2.5 opacity-70" aria-hidden />
      <span className="max-w-[12rem] truncate">{workstation.name}</span>
    </span>
  );
}
